/**
 * Shield system — pure math helpers for wave-15+ probabilistic shields.
 * No DOM, no EventBus, no enemy mutation inside the math functions.
 * The only side-effecting function is applyShield (sets fields on enemy).
 */
import type { Enemy } from "../entities/Enemy.ts";
import type { Rng } from "../core/Rng.ts";
import { FINAL_WAVE, SHIELD, SHIELD_START_WAVE } from "../core/config.ts";

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** Progress factor 0..1 clamped at FINAL_WAVE (held in endless). */
export function shieldProgress(wave: number): number {
  if (wave < SHIELD_START_WAVE) return 0;
  return clamp01((wave - SHIELD_START_WAVE) / (FINAL_WAVE - SHIELD_START_WAVE));
}

/** Shield spawn probability for a given wave. 0 below SHIELD_START_WAVE. */
export function shieldChance(wave: number): number {
  if (wave < SHIELD_START_WAVE) return 0;
  const p = shieldProgress(wave);
  return SHIELD.CHANCE_MIN + p * (SHIELD.CHANCE_MAX - SHIELD.CHANCE_MIN);
}

/** Shield HP for a given wave, as an absolute value based on baseMaxHp. */
export function shieldHpFor(baseMaxHp: number, wave: number): number {
  const p = shieldProgress(wave);
  const frac =
    SHIELD.HP_FRAC_MIN + p * (SHIELD.HP_FRAC_MAX - SHIELD.HP_FRAC_MIN);
  return Math.round(baseMaxHp * frac);
}

/**
 * Roll a shield for an eligible enemy (non-boss, wave >= SHIELD_START_WAVE).
 * The rng.next() call is UNCONDITIONAL for eligible spawns (draw first, branch
 * second) to keep the stream shape-preserving for future tweaks.
 *
 * @param isBoss - true if enemy is a boss (bosses are excluded)
 */
export function applyShield(
  enemy: Enemy,
  wave: number,
  baseMaxHp: number,
  rng: Rng,
  isBoss: boolean,
): void {
  if (isBoss || wave < SHIELD_START_WAVE) return;
  const roll = rng.next(); // unconditional draw
  const chance = shieldChance(wave);
  if (roll >= chance) return;
  const hp = shieldHpFor(baseMaxHp, wave);
  enemy.shieldHp = hp;
  enemy.shieldMax = hp;
}
